// @flow

import React, { type Node } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import {
  UniversalNav,
  UniversalNavPageLayout,
  useUniversalNavContext,
} from '@zapier/design-system-patterns';

import ContentFooter from 'web-ssr/app/common/components/ContentFooter';
import ContentHeader from 'web-ssr/app/common/components/ContentHeader';

/** @jsx isolateComponent */

type RenderSlotProps = {
  /**
   * Indicates whether `UniversalNav` should be rendered.
   */
  shouldRenderUniversalNav: boolean,
};

type Props = {
  /**
   * Content to render within the page.
   */
  children: Node,
  /**
   * Content to render within the header area of the page.
   */
  renderHeader: RenderSlotProps => Node,
  /**
   * Content to render within the footer area of the page.
   */
  renderFooter: RenderSlotProps => Node,
};

const GlobalLayout = (props: Props) => {
  const { shouldRenderUniversalNav } = useUniversalNavContext();
  // If `shouldRenderUniversalNav` then pass `undefined` to leverage
  // the default prop value to render the universal nav, otherwise
  // pass a function that prevents it from being rendered.
  const renderNav = shouldRenderUniversalNav ? undefined : () => null;
  const renderSlotProps = {
    shouldRenderUniversalNav,
  };
  return (
    <React.Fragment>
      {props.renderHeader(renderSlotProps)}
      <UniversalNavPageLayout renderNav={renderNav}>
        {props.children}
        {props.renderFooter(renderSlotProps)}
      </UniversalNavPageLayout>
    </React.Fragment>
  );
};

GlobalLayout.defaultProps = {
  renderHeader: ({ shouldRenderUniversalNav }) =>
    shouldRenderUniversalNav ? <UniversalNav /> : <ContentHeader />,
  renderFooter: () => <ContentFooter />,
};

export default GlobalLayout;
